"use client";

import Link from "next/link";
import useSWR from "swr";
import { fetcher } from "@/lib/api";

interface InvestorTrend {
  market: string;
  bizdate: string;
  personal: number;
  foreign: number;
  institutional: number;
  foreign_sell_streak?: number;  // 외국인 순매도 연속 영업일수
  link: string;
}
interface Disparity {
  label: string;
  symbol: string;
  price: number | null;
  ma50: number | null;
  disparity_pct: number | null;  // 50일MA 대비 현재가 초과율(%)
}
interface Regime {
  regime?: string;
  label?: string;
  high_vol?: boolean;
  metrics?: { price?: number; ma20?: number; ma60?: number; vol20?: number };
  ma_disparity?: Disparity[];
}

// 전역 상단 바 — 왼쪽: 당일 투자자별 매매동향(클릭 시 Naver), 오른쪽: 시장 국면(MarketRegime).
// 환율/코스피 라인(MarketIndices) 바로 아래에 배치되어 모든 페이지에 표시된다.
const IT_SWR = { refreshInterval: 60000 } as const;

export function MarketStatusBar() {
  // 투자자 동향은 시장별로 분리 표시 (코스피/코스닥/선물) — 훅은 조건부 호출 불가라 개별 선언
  const { data: kospiData } = useSWR<{ data: InvestorTrend | null }>(
    "/api/market/investor-trend?market=KOSPI", fetcher, IT_SWR
  );
  const { data: kosdaqData } = useSWR<{ data: InvestorTrend | null }>(
    "/api/market/investor-trend?market=KOSDAQ", fetcher, IT_SWR
  );
  const { data: futData } = useSWR<{ data: InvestorTrend | null }>(
    "/api/market/investor-trend?market=FUT", fetcher, IT_SWR
  );
  const { data: rgData } = useSWR<{ data: Regime }>(
    "/api/system/market-regime",
    fetcher,
    { refreshInterval: 600000 }
  );

  const trends = [
    { label: "코스피", d: kospiData?.data },
    { label: "코스닥", d: kosdaqData?.data },
    { label: "선물", d: futData?.data },
  ].filter((x): x is { label: string; d: InvestorTrend } => !!x.d);
  const regime = rgData?.data;
  const hasRegime = !!(regime && regime.regime && regime.regime !== "unknown");
  const disp = (regime?.ma_disparity || []).filter((x) => x.disparity_pct != null);
  const dispOver = disp.some((x) => (x.disparity_pct as number) > 30);  // 하나라도 30% 초과 → 경고
  if (trends.length === 0 && !hasRegime && disp.length === 0) return null;

  // 순매수(+)=빨강, 순매도(−)=파랑 (한국 관습)
  const cell = (label: string, v: number) => (
    <span className="inline-flex items-baseline gap-1 shrink-0">
      <span className="text-black/65">{label}</span>
      <span className={`font-medium ${v > 0 ? "text-[#ff453a]" : v < 0 ? "text-blue-600" : "text-black/80"}`}>
        {v > 0 ? "+" : ""}{v.toLocaleString()}
      </span>
    </span>
  );
  // 외국인 셀 — 순매도 7영업일 이상 연속이면 강조 (선물은 streak 집계 대상 아님 → 항상 0)
  const foreignCell = (d: InvestorTrend) => {
    const streak = typeof d.foreign_sell_streak === "number" ? d.foreign_sell_streak : 0;
    const hot = streak >= 7;
    return (
      <span
        className="inline-flex items-center gap-1.5 shrink-0 rounded px-1.5 py-0.5"
        style={hot ? { background: "rgba(48,209,88,0.55)" } : undefined}
        title={hot ? `외국인 순매도 ${streak}영업일 연속` : undefined}
      >
        {cell("외국인", d.foreign)}
        {hot && (
          <span className="text-xs font-bold px-1.5 py-0.5 rounded text-white bg-blue-600">
            {streak}일
          </span>
        )}
      </span>
    );
  };
  const regimeTip = regime?.metrics
    ? `KOSPI ${regime.metrics.price?.toLocaleString()} · MA20 ${regime.metrics.ma20?.toLocaleString()} · MA60 ${regime.metrics.ma60?.toLocaleString()} · 변동성 ${((regime.metrics.vol20 ?? 0) * 100).toFixed(1)}%/일`
    : undefined;

  return (
    <div
      className="w-full px-4 md:px-6 py-1.5 hidden md:flex landscape:flex items-center gap-3 overflow-x-auto border-b border-black/[0.04]"
      style={{ background: "#f5f5f7" }}
    >
      {trends.length > 0 && (
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-black/65 shrink-0">투자자 동향</span>
          {trends.map(({ label, d }) => (
            <button
              key={label}
              onClick={() => window.open(d.link, "_blank", "noopener,noreferrer")}
              className="flex items-center gap-2 text-xs tracking-[-0.08px] shrink-0 text-left rounded-md border border-black/[0.08] bg-white/70 px-2.5 py-1 hover:opacity-70 transition-opacity"
              title={`당일 투자자별 매매동향 (${label} 순매수, Naver) — 클릭 시 상세 페이지`}
            >
              <span className="text-black/65 shrink-0">{label}</span>
              {cell("개인", d.personal)}
              {cell("기관", d.institutional)}
              {foreignCell(d)}
            </button>
          ))}
        </div>
      )}
      <Link
        href="/foreign-radar"
        className="flex items-center gap-1 text-xs tracking-[-0.08px] shrink-0 rounded-md border border-black/[0.08] bg-white/70 px-2.5 py-1 text-black/65 hover:opacity-70 transition-opacity"
        title="외국인·글로벌 방향성 레이더 (당일 지수 편향)"
      >
        <span aria-hidden>📡</span>
        <span>외국인레이더</span>
      </Link>
      <div className="flex-1 min-w-0" />
      {hasRegime && (
        <div className="flex items-center gap-1.5 shrink-0" title={regimeTip}>
          <span
            className={`text-xs px-2.5 py-0.5 rounded-full font-semibold whitespace-nowrap ${
              regime!.regime === "bull"
                ? "bg-[#ff453a]/15 text-[#ff453a]"
                : regime!.regime === "bear"
                ? "bg-blue-500/15 text-blue-600"
                : "bg-black/[0.06] text-black/55"
            }`}
          >
            시장 {regime!.label}
          </span>
          {regime!.high_vol && (
            <span className="text-xs px-2 py-0.5 rounded-full font-semibold bg-amber-500/15 text-amber-600 whitespace-nowrap">
              ⚡고변동
            </span>
          )}
        </div>
      )}
      {/* MA50 이격률 박스 — 맨 오른쪽. 코스피·나스닥·금, 하나라도 +30% 초과 시 노란 바탕(과열 경고) */}
      {disp.length > 0 && (
        <div
          className="flex items-center gap-2.5 text-xs tracking-[-0.08px] shrink-0 rounded-md border border-black/[0.08] px-2.5 py-1"
          style={{ background: dispOver ? "rgba(255,235,59,0.65)" : "rgba(255,255,255,0.7)" }}
          title="50일 이동평균 대비 현재가 이격률 (코스피·나스닥·금, +30% 초과 시 과열 경고)"
        >
          <span className="text-black/65 shrink-0">MA50 이격</span>
          {disp.map((x) => (
            <span key={x.symbol} className="inline-flex items-baseline gap-1 shrink-0">
              <span className="text-black/55">{x.label}</span>
              <span className={`font-medium ${(x.disparity_pct as number) >= 0 ? "text-[#ff453a]" : "text-blue-600"}`}>
                {(x.disparity_pct as number) >= 0 ? "+" : ""}{x.disparity_pct}%
              </span>
            </span>
          ))}
          {dispOver && <span className="text-xs font-bold text-amber-700 shrink-0">⚠ 과열</span>}
        </div>
      )}
    </div>
  );
}
